import {ChangeDetectorRef, Component, Inject, inject, NgZone, OnInit} from '@angular/core';
import {MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {PromoProductOfferDialogComponent} from '../promo-product-offer-dialog.component';
import {THEME_CONFIG} from '../../../../../../environments/tokens/theme.token';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {DecimalPipe} from '@angular/common';

@Component({
  selector: 'app-promo-cart-bottom-sheet',
  templateUrl: './promo-cart-bottom-sheet.component.html',
  styleUrl: './promo-cart-bottom-sheet.component.scss',
  imports: [MatIcon, MatButton, DecimalPipe],
  standalone: true
})
export class PromoCartBottomSheetComponent implements OnInit {
  private bottomSheetRef = inject(MatBottomSheetRef<PromoCartBottomSheetComponent>);
  private dialog = inject(MatDialog);
  // ✅ FIX 1 : Injecter ChangeDetectorRef + NgZone pour forcer la détection de changements
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  protected readonly themeConfig = inject(THEME_CONFIG)

  // ✅ FIX 2 : Stocker des copies LOCALES des listes pour que le template réagisse aux changements
  buyItems: Array<{ product: any; quantity: number; rule: any }> = [];
  offeredItems: Array<{ product: any; quantity: number; rule: any }> = [];

  constructor(@Inject(MAT_BOTTOM_SHEET_DATA) public data: {
    promo: any;
    selectedItemQuantities: { [ruleId: string]: { [productId: string]: number } };
    customizedItems: { [productId: string]: { [index: number]: any } };
    customizableItems: { [productId: string]: boolean };
    productRequiredAdditions: { [productId: string]: boolean };
    fullProductsCache: { [productId: string]: any };
    parent: PromoProductOfferDialogComponent;
  }) {
  }

  ngOnInit() {
    // Charge les listes au démarrage
    this.refreshItems();
  }

  // ✅ FIX 3 : Méthode centrale pour rafraîchir les listes locales ET forcer la détection
  private refreshItems() {
    this.ngZone.run(() => {
      this.buyItems = this.data.parent.getSelectedBuyItems();
      this.offeredItems = this.data.parent.getOfferedItems();
      this.cdr.detectChanges();
    });
  }

  close() {
    this.bottomSheetRef.dismiss({customizedItems: this.data.customizedItems});
  }

  getCustomizationSummary(ruleId: string, productId: string, index: number = 0): string {
    return this.data.parent.getCustomizationSummary(ruleId, productId, index);
  }

  confirmAddToCart() {
    if (!this.canAdd()) return;
    this.bottomSheetRef.dismiss({action: 'addToCart', customizedItems: this.data.customizedItems});
  }

  // ✅ FIX 4 : getSelectedBuyItems / getOfferedItems retournent les listes LOCALES
  // (plus de délégation directe au parent qui ne trigger pas la détection)
  getSelectedBuyItems(): Array<{ product: any; quantity: number; rule: any }> {
    return this.buyItems;
  }

  getOfferedItems(): Array<{ product: any; quantity: number; rule: any }> {
    return this.offeredItems;
  }

  // ✅ FIX 5 : removeItem modifie le parent ET rafraîchit les listes locales immédiatement
  removeItem(ruleId: string, productId: string, index: number = 0) {
    this.data.parent.removeItem(ruleId, productId, index);
    // Rafraîchit les listes locales pour que le template se mette à jour
    this.refreshItems();
  }

  // ✅ FIX 6 : openCustomization — on ne ferme PLUS le BottomSheet
  // On ouvre le dialog de personnalisation directement, le BottomSheet reste ouvert
  // Quand le dialog se ferme, on rafraîchit les listes locales
  openCustomization(product: any, ruleId: string, index: number = 0) {
    this.data.parent.openProductCustomizationFromSheet(product, ruleId, index, () => {
      // Callback appelé quand le dialog de personnalisation se ferme
      this.refreshItems();
    });
  }

  isCustomized(ruleId: string, productId: string, index: number = 0): boolean {
    return this.data.parent.isItemCustomized(ruleId, productId, index);
  }


  getTotalBeforeReduction(): number {
    return this.data.parent.getTotalBeforeReduction()
  }

  hasIncompleteRequired(ruleId: string, productId: string, index: number = 0): boolean {
    return this.data.parent.hasIncompleteRequiredCustomizations(ruleId, productId, index);
  }

  getItemBasePrice(ruleId: string, productId: string, index: number = 0): number {
    return this.data.parent.getItemBasePriceOnly(ruleId, productId, index);
  }


  calculateTotal(): number {
    return this.data.parent.calculatePromotionTotal();
  }

  canAdd(): boolean {
    return this.data.parent.canAddToCart();
  }

  shouldShowStrikethroughPrice(ruleId: string, productId: string): boolean {
    return this.data.parent.shouldShowStrikethroughPrice(ruleId, productId);
  }

  shouldShowGreenPrice(ruleId: string, productId: string): boolean {
    return this.data.parent.shouldShowGreenPrice(ruleId, productId);
  }

  getItemDisplayPrice(ruleId: string, productId: string, index: number = 0): number {
    return this.data.parent.getItemDisplayPrice(ruleId, productId, index);
  }

  doesBuyItemsGroupHaveReduction(): boolean {
    return this.data.parent.doesBuyItemsGroupHaveReduction();
  }

  doesOfferedItemsGroupHaveReduction(): boolean {
    return this.data.parent.doesOfferedItemsGroupHaveReduction();
  }

  getFixedAmountReductionBadge(): string {
    return this.data.parent.getFixedAmountReductionBadge();
  }

  getReductionText(): string {
    return this.data.parent.getReductionText();
  }

  getTotalSupplementsPrice(): number {
    return this.data.parent.getTotalSupplementsPrice();
  }

  getTotalReductionAmount(): number {
    return this.data.parent.getTotalReductionAmount();
  }

  calculatePromotionTotal(): number {
    return this.data.parent.calculatePromotionTotal();
  }

  getValidationMessage(): string {
    return this.data.parent.getValidationMessage();
  }

  doesItemHaveReduction(ruleId: string, productId: string): boolean {
    return this.data.parent.doesItemHaveReduction(ruleId, productId)
  }

  getSizesText(sizes: any[]): string {
    return this.data.parent.getSizesText(sizes);
  }

  truncate2(value: number): number {
    return this.data.parent.truncate2(value);
  }
}
